import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  TableContainer,
  Paper
} from '@mui/material';
import { TagTooltip } from '../Common';
import { useJsonViewer } from '../../hooks/useJsonViewer';
import TimeEventJsonViewer from '../Timevents/TimeEventJsonViewer';
import { VariableSizeList as List } from 'react-window';
import Loading from '../LoadingSimple';
import EmptyState from '../EmptyState';
import EventListHeader from './EventListHeader';
import ColumnVisibilityFilter from './ColumnVisibilityFilter';

import { useDashboard } from '../Dashboard/DashboardContext';
import { getEventSummary } from '../../Utils/timeEvent';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { PerformanceAlert } from './PerformanceMonitor';
import { parseEventDate } from '../../Utils/dateUtils';

// Import all viewer components
import PDFViewer from '../Viewer/PDFViewer';
import ImageViewer from '../Viewer/ImageViewer';
import AudioViewer from '../Viewer/AudioViewer';
import ExcelViewer from '../Viewer/ExcelViewer';
import HTMLViewer from '../Viewer/HTMLViewer';
import TextViewer from '../Viewer/TextViewer';
import VideoViewer from '../Viewer/VideoViewer';
import EmailViewer from '../Viewer/EmailViewer';
import ZipViewer from '../Viewer/ZipViewer';
import WordViewer from '../Viewer/WordViewer';
import PowerPointViewer from '../Viewer/PowerPointViewer';
import CSVViewer from '../Viewer/CSVViewer';
import OtherViewer from '../Viewer/OtherViewer';

// Default row height for compact display
const DEFAULT_ROW_HEIGHT = 40;

// Maximum number of rows the EventList can stream
const LINE_LIMIT_EVENT_THRESHOLD = 2000;

// Unified file type configuration - replaces 12+ separate format checker functions
const FILE_TYPES = {
  pdf: { extensions: ['.pdf'], component: 'PDFViewer' },
  image: { extensions: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg'], component: 'ImageViewer' },
  audio: { extensions: ['.mp3', '.wav', '.aac', '.flac', '.ogg', '.wma', '.m4a'], component: 'AudioViewer' },
  excel: { extensions: ['.xlsx', '.xls', '.xlsm'], component: 'ExcelViewer' },
  html: { extensions: ['.html', '.htm'], component: 'HTMLViewer' },
  text: { extensions: ['.txt', '.ini', '.hl7', '.log', '.md', '.xml', '.js', '.py', '.java', '.cpp', '.cs', '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.ts', '.jsx', '.tsx', '.vue', '.css', '.scss', '.sass', '.less', '.sql', '.sh', '.bat', '.ps1', '.yaml', '.yml', '.toml', '.cfg', '.conf', '.properties'], component: 'TextViewer' },
  video: { extensions: ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v', '.3gp', '.m3u8'], component: 'VideoViewer' },
  email: { extensions: ['.msg', '.eml'], component: 'EmailViewer' },
  zip: { extensions: ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'], component: 'ZipViewer' },
  word: { extensions: ['.docx', '.doc'], component: 'WordViewer' },
  powerpoint: { extensions: ['.pptx', '.ppt'], component: 'PowerPointViewer' },
  csv: { extensions: ['.csv'], component: 'CSVViewer' },
  json: { extensions: ['.json'], component: 'JSONViewer' }
};

// Optimized file type detector - replaces 12+ individual functions
const getFileType = (filename) => {
  if (!filename) return 'other';
  const ext = filename.toLowerCase();
  return Object.keys(FILE_TYPES).find(type => 
    FILE_TYPES[type].extensions.some(extension => ext.endsWith(extension))
  ) || 'other';
};

// Debounce utility for performance optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Format column header text
const formatColumnHeader = (column) => {
  // Handle special cases
  const specialCases = {
    'eventtime': 'Time',
    'eventsummary': 'Summary',
    'eventsource': 'Source'
  };
  
  if (specialCases[column]) {
    return specialCases[column];
  }
  
  // Convert camelCase to Title Case
  return column
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Virtual Table Header Component for performance mode

// Combined Virtual Item Component with unified column rendering
const VirtualTableItem = React.memo(({ index, style, data }) => {
  // Common cell style for both header and data cells
  const getCellStyle = (column, isHeader = false) => ({
    width: data.getColumnWidth(column),
    minWidth: data.getColumnWidth(column),
    maxWidth: data.getColumnWidth(column),
    boxSizing: 'border-box',
    padding: '6px 8px',
    borderRight: '1px solid #f0f0f0',
    borderBottom: isHeader ? '2px solid #bdbdbd' : '1px solid #e0e0e0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: isHeader ? 15 : 14,
    fontWeight: isHeader ? 'bold' : 'normal',
    background: isHeader ? 'linear-gradient(90deg, #f5f7fa 60%, #e3e6f3 100%)' : 'inherit',
    display: 'flex',
    alignItems: 'center',
    position: 'relative'
  });

  // Common row container style
  const rowStyle = {
    display: 'flex',
    width: data.totalTableWidth,
    minWidth: data.totalTableWidth,
    height: '100%'
  };

  if (index === 0) {
    // Render header row with unified styling
    return (
      <div style={style}>
        <div style={rowStyle}>
          {data.columns.map((col) => (
            <div
              key={col}
              style={getCellStyle(col, true)}
              onClick={() => data.onColumnHeaderClick && data.onColumnHeaderClick(col)}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                flex: 1,
                cursor: 'pointer'
              }}>
                <span style={{ flex: 1 }}>
                  {formatColumnHeader(col)}
                </span>
                {/* Sort indicator */}
                {data.sortColumn === col && (
                  <span style={{ 
                    marginLeft: '4px', 
                    fontSize: '12px',
                    color: '#1976d2'
                  }}>
                    {data.sortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </div>
              {/* Resize handle */}
              {data.onResizeStart && (
                <div
                  className="resize-handle"
                  style={{
                    position: 'absolute',
                    right: '-8px',
                    top: 0,
                    bottom: 0,
                    width: '16px',
                    cursor: 'col-resize',
                    backgroundColor: 'transparent',
                    zIndex: 1100
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    data.onResizeStart(e, col);
                  }}
                  title={`Resize ${col} column`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Render data row with unified styling
  const eventIndex = index - 1;
  const event = data.events[eventIndex];
  const currentRowHeight = data.tempRowHeights[event.id] || data.rowHeights[event.id] || DEFAULT_ROW_HEIGHT;

  return (
    <div style={style}>
      <div style={{
        ...rowStyle,
        backgroundColor: eventIndex % 2 === 0 ? '#fafbff' : '#f0f2fa',
        transition: 'background 0.2s',
        minHeight: currentRowHeight,
        height: currentRowHeight
      }}>
        {data.columns.map((col) => {
          // Optimize tag variable handling to avoid double find() calls
          let value, tagObject = null;
          const isTagVariable = data.allTagVariables.includes(col);
          
          if (isTagVariable) {
            const { value: tagValue, tagObject: foundTag } = data.getTagValueAndObject(event, col);
            value = tagValue;
            tagObject = foundTag;
          } else {
            value = event[col];
          }
          
          if (col === 'eventtime' && value) {
            value = parseEventDate(value).toLocaleString();
          }

          const isEditing = data.isEditing(event.id, col);
          const hasFile = col === 'eventsource' && value;
          const canEdit = ['eventname', 'eventdescription', 'eventtime'].includes(col);
          
          // Function to check if value is JSON (either already parsed object/array or JSON string)
          const isJsonValue = (val) => {
            // If it's already an object or array, it's JSON-like
            if (typeof val === 'object' && val !== null) {
              return true;
            }
            // If it's a string, try to parse it
            if (typeof val === 'string') {
              try {
                const parsed = JSON.parse(val);
                return (typeof parsed === 'object' && parsed !== null) || Array.isArray(parsed);
              } catch (e) {
                return false;
              }
            }
            return false;
          };
          
          const shouldShowRichTooltip = isTagVariable || (value && (value.length > 40 || isJsonValue(value)));
          
          const cellContent = (
            <div
              key={col}
              style={{
                ...getCellStyle(col, false),
                cursor: isEditing ? 'default' : (hasFile ? 'pointer' : (canEdit ? 'text' : 'default')),
                color: value ? '#222' : '#888',
                whiteSpace: currentRowHeight <= 50 ? 'nowrap' : 'pre-wrap',
                overflow: currentRowHeight <= 50 ? 'hidden' : 'visible',
                height: currentRowHeight
              }}
              onClick={() => data.handleCellClick(event, col, value)}
            >
              {isEditing ? (
                // Determine if this is likely JSON content for better editing experience
                data.editValue && (
                  (data.editValue.trim().startsWith('{') && data.editValue.trim().endsWith('}')) ||
                  (data.editValue.trim().startsWith('[') && data.editValue.trim().endsWith(']'))
                ) ? (
                  // Use textarea for JSON editing
                  <textarea
                    value={data.editValue}
                    onChange={(e) => data.setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        data.saveEdit();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        data.cancelEditing();
                      }
                    }}
                    onBlur={data.saveEdit}
                    style={{
                      width: '100%',
                      height: Math.max(60, currentRowHeight - 12),
                      border: '1px solid #ccc',
                      outline: 'none',
                      background: '#f9f9f9',
                      fontSize: 'inherit',
                      fontFamily: 'monospace',
                      padding: '4px',
                      resize: 'none',
                      whiteSpace: 'pre'
                    }}
                    placeholder="Enter JSON (Ctrl+Enter to save, Esc to cancel)"
                    autoFocus
                  />
                ) : (
                  // Use regular input for non-JSON content
                  <input
                    type="text"
                    value={data.editValue}
                    onChange={(e) => data.setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        data.saveEdit();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        data.cancelEditing();
                      }
                    }}
                    onBlur={data.saveEdit}
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                    autoFocus
                  />
                )
              ) : (
                <span style={{ 
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {typeof value === 'object' && value !== null ? 
                    JSON.stringify(value) : (value || '')}
                </span>
              )}
            </div>
          );
          
          // Return with TagTooltip for tag variables and long values
          if (shouldShowRichTooltip && !isEditing) {
            return (
              <TagTooltip 
                key={col}
                data={value}
                title={formatColumnHeader(col)}
                dataType="auto"
                showActions={true}
                onShowJsonViewer={() => {
                  // Use the already-found tagObject to avoid redundant find() calls
                  // tagObject is already available from the optimized getTagValueAndObject call above
                  
                  // Open JSON viewer for structured data
                  data.openTimeEventJsonViewer({
                    title: `${formatColumnHeader(col)} - Event ${event.id}`,
                    data: value,
                    fieldId: tagObject ? tagObject.id : null,
                    onSave: tagObject ? data.handleTimeEventJsonSave : null, // Enable save for tag variables
                    caseid: data.caseid,
                    tag: tagObject // Pass the full tag object for server saves
                  });
                }}
                customStyles={{
                  '& .MuiTooltip-tooltip': {
                    maxWidth: '450px',
                    maxHeight: '350px'
                  }
                }}
              >
                {cellContent}
              </TagTooltip>
            );
          }
          
          // Return simple cell for basic cases
          return cellContent;
        })}
      </div>
    </div>
  );
});

const EventList = ({ caseid, folder = '' }) => {
  const { setFilteredTimeEvents, setTimeEvents } = useDashboard();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const { openSnackbar } = useSnackbar();
  
  // Server-side data state
  const [timeevents, setTimeEventsLocal] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allTagVariables, setAllTagVariables] = useState([]);
  const [lineLimitExceeded, setLineLimitExceeded] = useState(false);
  const [lineLimitMessage, setLineLimitMessage] = useState('');
  const [lineLimitCount, setLineLimitCount] = useState(0);
  const [lineLimitThreshold, setLineLimitThreshold] = useState(LINE_LIMIT_EVENT_THRESHOLD);
  const tableContainerRef = useRef(null);
  const horizontalScrollRef = useRef(null);

  // JSON Viewer functionality using reusable hook
  const {
    isOpen: timeEventJsonViewerOpen,
    viewerData: timeEventJsonData,
    viewerTitle: timeEventJsonTitle,
    viewerFieldId: timeEventJsonFieldId,
    viewerCaseid: timeEventJsonCaseid,
    viewerTag: timeEventJsonTag, // Add tag destructuring
    openJsonViewer: openTimeEventJsonViewer,
    closeJsonViewer: closeTimeEventJsonViewer,
    handleSaveJson: handleTimeEventJsonSave
  } = useJsonViewer();



  // Calculate available viewport height for virtual scrolling
  const calculateVirtualScrollHeight = useCallback(() => {
    // Get viewport height
    const viewportHeight = window.innerHeight;
    // Estimate space taken by header, filters, and other UI elements (approximate)
    const uiOffset = 280; // Adjust based on your UI layout
    // Return available height for virtual scrolling, with minimum
    return Math.max(400, viewportHeight - uiOffset);
  }, []);

  // Item size function for virtual scrolling - header is taller than data rows
  const getItemSize = useCallback((index) => {
    return index === 0 ? 56 : DEFAULT_ROW_HEIGHT; // Header is 56px, data rows are DEFAULT_ROW_HEIGHT
  }, []);
  
  const handleHorizontalTrackScroll = useCallback((event) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = event.target.scrollLeft;
    }
  }, []);

  const syncHorizontalScroll = useCallback(() => {
    if (tableContainerRef.current && horizontalScrollRef.current) {
      horizontalScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', syncHorizontalScroll, { passive: true });
    syncHorizontalScroll();
    return () => container.removeEventListener('scroll', syncHorizontalScroll);
  }, [syncHorizontalScroll]);

  
  // Unified file viewer state - replaces 30+ individual useState calls
  const [fileViewer, setFileViewer] = useState({
    open: false,
    type: null,
    filename: '',
    eventId: null
  });
  
  // Core application state
  const [filter, setFilter] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenEventCount, setHiddenEventCount] = useState(0);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortColumn, setSortColumn] = useState('eventtime');
  const [hideOtherTags, setHideOtherTags] = useState(false);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState([]);
  
  // Editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [originalValue, setOriginalValue] = useState('');

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null);
  const [tempColumnWidths, setTempColumnWidths] = useState({});
  
  // Row resizing state
  const [rowHeights] = useState({});
  const [tempRowHeights, setTempRowHeights] = useState({});
  
  // Debounced filter for better performance with large datasets
  const debouncedFilter = useDebounce(filter, 300);

  // Fetch events from server
  const fetchEvents = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !caseid) return;
    
    setLoading(true);
    sendMessage({
      command: 'getFilteredTimeEvents',
      caseid: parseInt(caseid),
      page: 1,
      pageSize: 100000, // Fetch all for EventList since it uses virtual scrolling
      filter: debouncedFilter,
      showHidden,
      sortBy: 'date',
      sortOrder: sortOrder,
      tagVariable: ''
    });
  }, [ws, caseid, debouncedFilter, showHidden, sortOrder, sendMessage]);

  // Listen for server response
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.command === 'getFilteredTimeEvents') {
          if (message.data && message.data.error) {
            console.error('Error fetching events:', message.data.error);
            setLoading(false);
            return;
          }

          // Verify case ID matches (security check)
          if (message.data && message.data.caseid !== parseInt(caseid)) {
            console.warn('Received events for different case, ignoring');
            return;
          }

          if (message.data?.lineLimitExceeded) {
            const count = message.data.totalCount || 0;
            const threshold = message.data.lineLimit || LINE_LIMIT_EVENT_THRESHOLD;
            const fallbackMessage = `Dataset contains ${count.toLocaleString()} rows, which exceeds the ${threshold.toLocaleString()} row limit. Please narrow your filters to view the data.`;

            setLineLimitExceeded(true);
            setLineLimitMessage(message.data.message || fallbackMessage);
            setLineLimitCount(count);
            setLineLimitThreshold(threshold);
            setTimeEventsLocal([]);
            setTimeEvents([]);
            setTotalCount(count);
            setHiddenEventCount(0);
            setAllTagVariables([]);
            setLoading(false);
            return;
          }

          setLineLimitExceeded(false);
          setLineLimitMessage('');
          setLineLimitCount(0);
          setLineLimitThreshold(LINE_LIMIT_EVENT_THRESHOLD);

          const events = message.data?.events || [];
          console.log('EventList received events:', events.length, 'total:', message.data?.totalCount);
          setTimeEventsLocal(events); // Set local state
          setTimeEvents(events); // Update context for compatibility
          setTotalCount(message.data?.totalCount || 0);
          setHiddenEventCount(message.data?.hiddenCount || 0);
          setAllTagVariables(message.data?.availableTagVariables || []);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        setLoading(false);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws, caseid, setTimeEvents]);

  // Fetch events when filters change
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Unified file opener - replaces 80+ lines of repetitive if/else chains
  const handleFileOpen = useCallback((event) => {
    const { id, eventsource, isExist } = event;
    
    if (!eventsource || !isExist) {
      openSnackbar('File not found or not available', 'warning');
      return;
    }
    
    const filename = eventsource.split(/[\\\\/]/).pop();
    const fileType = getFileType(eventsource);
    
    // Handle JSON files specially with inline viewer
    if (fileType === 'json') {
      ws.send(JSON.stringify({ command: 'openfile', id }));
      openSnackbar('JSON file opened. Check the file viewers.', 2000, 'info');
      return;
    }
    
    // Open unified file viewer
    setFileViewer({ open: true, type: fileType, filename, eventId: id });
    ws.send(JSON.stringify({ command: 'openfile', id }));
  }, [ws, openSnackbar]);

  // Unified file viewer close handler
  const closeFileViewer = useCallback(() => {
    setFileViewer({ open: false, type: null, filename: '', eventId: null });
  }, []);

  // Server already filtered the events, so we just use them directly
  const filteredEvents = useMemo(() => {
    return timeevents.map(event => ({
      ...event,
      eventsummary: getEventSummary(event)
    })); // Already filtered by server
  }, [timeevents]);

  // Update context when filtered events change
  useEffect(() => {
    setFilteredTimeEvents(filteredEvents);
  }, [filteredEvents, setFilteredTimeEvents]);

  const tableColumns = useMemo(() => {
    const standardFields = ['eventtime', 'eventsummary', 'eventsource'];
    // Use server-provided tag variables
    return standardFields.concat(allTagVariables);
  }, [allTagVariables]);

  // Initialize visible columns when tableColumns changes
  useEffect(() => {
    // Only set visibleColumns if it is empty (first load)
    if (tableColumns.length > 0 && visibleColumns.length === 0) {
      // Show all columns by default
      setVisibleColumns(tableColumns);
    }
  }, [tableColumns, visibleColumns.length]);

  // Filter columns based on visibility
  const displayColumns = useMemo(() => {
    return tableColumns.filter(col => visibleColumns.includes(col));
  }, [tableColumns, visibleColumns]);

  // Optimized callback functions with useCallback for better performance
  // Optimized function that returns both value and tag object to avoid double find() calls
  const getTagValueAndObject = useCallback((event, tagVariable) => {
    if (!event.customFields || !tagVariable) return { value: '', tagObject: null };
    const field = event.customFields.find((f) => f.variable === tagVariable);
    if (!field || !field.reply) return { value: '', tagObject: field || null };
    
    let value = '';
    if (Array.isArray(field.reply)) {
      // If it's already an array, keep it as array
      value = field.reply;
    } else if (typeof field.reply === 'object' && field.reply !== null) {
      // If it's already an object, keep it as object
      value = field.reply;
    } else {
      // Try to parse JSON strings back to objects/arrays for proper display
      const replyStr = field.reply.toString();
      try {
        // Check if it looks like JSON and can be parsed
        if ((replyStr.startsWith('{') && replyStr.endsWith('}')) || 
            (replyStr.startsWith('[') && replyStr.endsWith(']'))) {
          const parsed = JSON.parse(replyStr);
          // If successfully parsed and is an object or array, return the parsed value
          if (typeof parsed === 'object' && parsed !== null) {
            value = parsed;
          } else {
            value = replyStr;
          }
        } else {
          value = replyStr;
        }
      } catch (e) {
        // If parsing fails, treat as regular string
        value = replyStr;
      }
    }
    
    return { value, tagObject: field };
  }, []);

  const getTagValue = useCallback((event, tagVariable) => {
    return getTagValueAndObject(event, tagVariable).value;
  }, [getTagValueAndObject]);

  const getTagValueForTable = useCallback((event, tagVariable) => getTagValue(event, tagVariable), [getTagValue]);

  // Optimized callback functions for better performance
  const handleCheckboxChange = useCallback((e) => setShowHidden(e.target.checked), []);
  const handleToggleSortOrder = useCallback(() => setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc')), []);
  const handleColumnHeaderClick = useCallback((column) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as sort column with ascending order
      setSortColumn(column);
      setSortOrder('asc');
    }
  }, [sortColumn, sortOrder]);

  // Optimized editing functions with useCallback
  const startEditing = useCallback((eventId, column, currentValue) => {
    setEditingCell({ eventId, column });
    
    // Handle JSON objects/arrays properly for editing
    let editableValue = currentValue || '';
    if (typeof currentValue === 'object' && currentValue !== null) {
      editableValue = JSON.stringify(currentValue, null, 2);
    }
    
    setEditValue(editableValue);
    setOriginalValue(editableValue);
  }, []);

  // Modified table cell click handler to distinguish between edit and file open
  const handleCellClick = useCallback((event, col, value) => {
    if (col === 'eventsource') {
      // For eventsource column, only allow file opening, never editing
      if (value && event.isExist) {
        handleFileOpen(event);
      } else {
        console.log('File not found or no eventsource for event:', event.id);
        openSnackbar('File not found or not available', 'warning');
      }
    } else if (!editingCell) {
      // For other columns, allow editing as usual
      startEditing(event.id, col, value);
    }
  }, [editingCell, handleFileOpen, startEditing, openSnackbar]);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
    setOriginalValue('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingCell || editValue === originalValue) {
      cancelEditing();
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not available for saving');
      cancelEditing();
      return;
    }

    const { eventId, column } = editingCell;

    try {
      // Prepare the value to send - try to parse JSON if it looks like JSON
      let valueToSend = editValue;
      if (allTagVariables.includes(column)) {
        // For tag variables, try to parse JSON if the value looks like JSON
        if (typeof editValue === 'string' && 
            ((editValue.trim().startsWith('{') && editValue.trim().endsWith('}')) ||
             (editValue.trim().startsWith('[') && editValue.trim().endsWith(']')))) {
          try {
            valueToSend = JSON.parse(editValue);
          } catch (e) {
            // If parsing fails, keep as string
            console.log('JSON parsing failed, keeping as string:', e.message);
          }
        }
      }
      
      if (allTagVariables.includes(column)) {
        // Handle custom field (tag variable) updates
        const event = timeevents.find(e => e.id === eventId);
        if (!event) {
          console.error('Event not found for editing');
          cancelEditing();
          return;
        }

        // Find existing custom field or create new one
        let existingField = null;
        if (event.customFields) {
          existingField = event.customFields.find(f => f.variable === column);
        }

        if (existingField) {
          // Update existing custom field
          const updatedField = { ...existingField, reply: valueToSend };
          ws.send(JSON.stringify({ 
            command: 'addUpdateVariable', 
            id: existingField.id, 
            tag: updatedField 
          }));
        } else {
          // Create new custom field - this requires the tag structure
          const newField = {
            id: null, // Will be assigned by server
            timeeventid: eventId,
            variable: column,
            description: column, // Use variable name as description for new fields
            reply: valueToSend,
            done: 1,
            icon: 'document.svg',
            type: 'text'
          };
          ws.send(JSON.stringify({ 
            command: 'addUpdateVariable', 
            id: null, 
            tag: newField 
          }));
        }
      } else {
        // Handle standard field updates
        switch (column) {
          case 'eventsource':
            // Note: eventsource might not be editable in the backend
            console.warn('Editing eventsource may not be supported');
            break;
          case 'eventtime':
            // Note: eventtime has a specific format and handler
            ws.send(JSON.stringify({ 
              command: 'updateDateForEvent', 
              id: eventId, 
              date: editValue 
            }));
            break;
          default:
            console.warn('Unsupported field for editing:', column);
            break;
        }
      }

      // Update local state optimistically
      const updatedEvents = timeevents.map(event => {
        if (event.id === eventId) {
          if (allTagVariables.includes(column)) {
            // Update custom field
            const updatedEvent = { ...event };
            if (!updatedEvent.customFields) {
              updatedEvent.customFields = [];
            }
            
            const fieldIndex = updatedEvent.customFields.findIndex(f => f.variable === column);
            if (fieldIndex >= 0) {
              updatedEvent.customFields[fieldIndex] = {
                ...updatedEvent.customFields[fieldIndex],
                reply: valueToSend
              };
            } else {
              updatedEvent.customFields.push({
                variable: column,
                reply: valueToSend,
                description: column,
                done: 1,
                icon: 'document.svg',
                type: 'text'
              });
            }
            return updatedEvent;
          } else {
            // Update standard field
            return {
              ...event,
              [column]: editValue
            };
          }
        }
        return event;
      });

      // Update the context with modified events
      setTimeEvents(updatedEvents);
      
      // Show success message
      openSnackbar('Field updated successfully', 2000, 'success');
      
      cancelEditing();
    } catch (error) {
      console.error('Error saving edit:', error);
      openSnackbar('Error saving changes', 3000, 'error');
      cancelEditing();
    }
  }, [editingCell, editValue, originalValue, ws, allTagVariables, timeevents, setTimeEvents, openSnackbar, cancelEditing]);

  // Column resizing functions - using stored state instead of DOM measurements
  const handleResizeStart = useCallback((e, column) => {
    console.log('🔧 Column resize start triggered for column:', column);
    e.preventDefault();
    e.stopPropagation();
    
    // Disable text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    
    // Use stored column width instead of DOM measurements for reliability
    const currentWidth = tempColumnWidths[column] || columnWidths[column] || 150;
    console.log('📐 Using stored width for column:', column, currentWidth);
    
    setResizing({
      type: 'column',
      column,
      startX: e.clientX,
      startWidth: currentWidth
    });
  }, [columnWidths, tempColumnWidths]);

  const handleRowResizeStart = useCallback((e, rowId) => {
    console.log('🔧 Row resize start triggered for row:', rowId);
    e.preventDefault();
    e.stopPropagation();
    
    // Disable text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    
    const rect = e.currentTarget.closest('tr').getBoundingClientRect();
    console.log('📏 Initial height:', rect.height);
    
    setResizing({
      type: 'row',
      row: rowId,
      startY: e.clientY,
      startHeight: rect.height
    });
  }, []);

  // Throttled resize move for better performance
  const handleResizeMove = React.useCallback((e) => {
    if (!resizing) return;
    
    // Throttle resize updates for better performance
    if (resizing.lastUpdate && Date.now() - resizing.lastUpdate < 16) return; // ~60fps
    
    if (resizing.type === 'column') {
      const deltaX = e.clientX - resizing.startX;
      const newWidth = Math.max(80, resizing.startWidth + deltaX);
      
      console.log('🔄 Resizing column:', resizing.column, 'deltaX:', deltaX, 'newWidth:', newWidth);
      
      // Use temp widths for smooth preview during resize - no heavy table re-render
      setTempColumnWidths(prev => ({
        ...prev,
        [resizing.column]: newWidth
      }));
    } else if (resizing.type === 'row') {
      const deltaY = e.clientY - resizing.startY;
      const newHeight = Math.max(32, resizing.startHeight + deltaY);
      
      console.log('🔄 Resizing row:', resizing.row, 'deltaY:', deltaY, 'newHeight:', newHeight);
      
      // Use temp heights for smooth preview during resize
      setTempRowHeights(prev => ({
        ...prev,
        [resizing.row]: newHeight
      }));
    }
    
    // Update last update timestamp
    setResizing(prev => ({ ...prev, lastUpdate: Date.now() }));
  }, [resizing]);

  const handleResizeEnd = React.useCallback(() => {
    console.log('✅ Resize end triggered');
    
    // Restore text selection
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.msUserSelect = '';
    
    if (resizing && tempColumnWidths[resizing.column]) {
      // Apply the final width to permanent state
      setColumnWidths(prev => ({
        ...prev,
        [resizing.column]: tempColumnWidths[resizing.column]
      }));
      // Clear temp width
      setTempColumnWidths(prev => {
        const updated = { ...prev };
        delete updated[resizing.column];
        return updated;
      });
    }
    setResizing(null);
  }, [resizing, tempColumnWidths]);

  // Add mouse event listeners for resizing
  React.useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Get column width - optimized with memoization
  const getColumnWidth = useCallback((column) => {
    // Use temp width during resize for smooth preview, otherwise use permanent width
    const effectiveWidth = tempColumnWidths[column] || columnWidths[column] || 150;
    return `${effectiveWidth}px`;
  }, [tempColumnWidths, columnWidths]);

  // Calculate total table width for proper horizontal scrolling - optimized
  const totalTableWidthValue = useMemo(() => {
    return displayColumns.reduce((sum, column) => {
      const effectiveWidth = tempColumnWidths[column] || columnWidths[column] || 150;
      return sum + effectiveWidth;
    }, 0);
  }, [displayColumns, columnWidths, tempColumnWidths]);

  const totalTableWidth = `${totalTableWidthValue}px`;

  // Add WebSocket message listener for server responses
  useEffect(() => {
    if (!subscribeToMessages) return;

    const unsubscribe = subscribeToMessages((parsedMessage) => {
      const { command, data, caseid: messageCaseId } = parsedMessage;
      
      // Validate case ID to prevent cross-contamination of data from different cases
      if (messageCaseId && messageCaseId !== caseid) {
        console.warn(`🚫 Ignoring message for case ${messageCaseId}, current case is ${caseid}`);
        return;
      }
      
      // Handle server responses for various update commands
      switch (command) {
        case 'addUpdateVariable':
          if (data === 'success') {
            // Custom field update was successful
            console.log('✅ Custom field updated successfully on server for case:', caseid);
          }
          break;
        case 'updateDateForEvent':
          console.log('✅ Date updated successfully on server for case:', caseid);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, caseid]);

  // Create sorted events for table display
  const sortedEventsForTable = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      let valueA, valueB;
      
      // Get values based on sort column
      if (allTagVariables.includes(sortColumn)) {
        // Sorting by tag variable
        valueA = getTagValue(a, sortColumn) || '';
        valueB = getTagValue(b, sortColumn) || '';
      } else {
        // Sorting by standard field
        valueA = a[sortColumn] || '';
        valueB = b[sortColumn] || '';
      }
      
      // Handle special cases for different column types
      if (sortColumn === 'eventtime') {
        const dateA = parseEventDate(valueA);
        const dateB = parseEventDate(valueB);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortColumn === 'id') {
        const numA = parseInt(valueA, 10);
        const numB = parseInt(valueB, 10);
        return sortOrder === 'asc' ? numA - numB : numB - numA;
      } else {
        // Try numeric comparison first
        const numA = parseFloat(valueA);
        const numB = parseFloat(valueB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }
        // Fall back to string comparison
        const comparison = String(valueA).localeCompare(String(valueB));
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    });
  }, [filteredEvents, sortColumn, sortOrder, allTagVariables, getTagValue]);

  // Show loading state while fetching data
  if (loading) {
    return (
      <Grid container spacing={0} sx={{ 
        mt: 0, 
        width: '100%', 
        maxWidth: '100%',
        minHeight: '100vh', 
        mx: 0,
        overflowX: 'hidden'
      }}>
        <Grid item xs={12} sx={{ 
          px: 0,
          maxWidth: '100%',
          width: '100%'
        }}>
          <Card sx={{ 
            borderRadius: 0, 
            boxShadow: 'none', 
            width: '100%',
            maxWidth: '100%'
          }}>
            <CardContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Loading />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  // Show empty state only when not loading and no events
  if (!timeevents || timeevents.length === 0) {
    return (
      <Grid container spacing={0} sx={{ 
        mt: 0, 
        width: '100%', 
        maxWidth: '100%',
        minHeight: '100vh', 
        mx: 0,
        overflowX: 'hidden'
      }}>
        <Grid item xs={12} sx={{ 
          px: 0,
          maxWidth: '100%',
          width: '100%'
        }}>
          <Card sx={{ 
            borderRadius: 0, 
            boxShadow: 'none', 
            width: '100%',
            maxWidth: '100%'
          }}>
            <CardContent sx={{ p: 0 }}>
              <EmptyState type="eventlist" caseid={caseid} showAction={false} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} sx={{ 
      mt: 0, 
      width: '100%', 
      maxWidth: '100%',
      mx: 0,
      overflowX: 'hidden' 
    }}>
      <Grid item xs={12} sx={{ 
        px: 0,
        maxWidth: '100%',
        width: '100%'
      }}>
        <Card sx={{ 
          borderRadius: 0, 
          boxShadow: 'none', 
          width: '100%',
          maxWidth: '100%'
        }}>
          <CardContent sx={{ 
            p: 0.5, 
            pb: 1.5, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <EventListHeader
              filter={filter}
              onFilterChange={setFilter}
              onClearFilter={() => setFilter('')}
              filteredTimeEvents={filteredEvents}
              totalCount={totalCount}
              showHidden={showHidden}
              onCheckboxChange={handleCheckboxChange}
              hiddenEventCount={hiddenEventCount}
              sortOrder={sortOrder}
              onToggleSortOrder={handleToggleSortOrder}
              caseid={caseid}
              hideOtherTags={hideOtherTags}
              onHideOtherTagsChange={setHideOtherTags}
            />
            
            {/* Column Visibility Filter */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1, px: 1 }}>
              <ColumnVisibilityFilter
                columns={tableColumns}
                visibleColumns={visibleColumns}
                onVisibilityChange={setVisibleColumns}
                standardColumns={['eventtime', 'eventsummary', 'eventsource']}
              />
            </Box>

            {lineLimitExceeded ? (
              <Box sx={{ mt: 2, width: '100%' }}>
                <Alert severity="warning" sx={{ lineHeight: 1.25 }}>
                  {lineLimitMessage || (
                    <>
                      Dataset exceeds the {lineLimitThreshold?.toLocaleString()} row limit. Narrow your filters to view the data.
                    </>
                  )}
                  {lineLimitCount > 0 && (
                    <Box sx={{ mt: 1, fontSize: '0.9rem', color: 'rgba(0,0,0,0.7)' }}>
                      Showing warning for {lineLimitCount.toLocaleString()} rows (limit {lineLimitThreshold?.toLocaleString()}).
                    </Box>
                  )}
                </Alert>
              </Box>
            ) : (
              <>
                {/* Performance Alert for large datasets */}
                <PerformanceAlert 
                  dataSize={filteredEvents.length}
                  onOptimize={() => openSnackbar(`Handling ${filteredEvents.length} rows with virtual scrolling`, 3000, 'info')}
                />

                <Box
                  ref={horizontalScrollRef}
                  onScroll={handleHorizontalTrackScroll}
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    height: '16px',
                    mb: 1,
                    display: totalTableWidthValue > 0 ? 'block' : 'none',
                    '&::-webkit-scrollbar': {
                      height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: 4,
                      '&:hover': {
                        backgroundColor: '#a8a8a8',
                      },
                    }
                  }}
                >
                  <Box sx={{ width: totalTableWidthValue, height: '1px' }} />
                </Box>

                <Box>
                  <Box sx={{ 
                    width: '100%', 
                    mt: 1, 
                    mb: 2,
                    position: 'relative',
                    overflow: 'visible'
                  }}>
                    {/* Always use Virtual Scrolling for Optimal Performance */}
                <TableContainer 
                  component={Paper} 
                  ref={tableContainerRef}
                  sx={{ 
                        width: '100%', 
                        height: calculateVirtualScrollHeight(), // Use maximum available height
                        boxShadow: 2,
                        overflowX: 'auto',
                        overflowY: 'hidden', // Let react-window handle vertical scrolling
                        userSelect: resizing ? 'none' : 'auto',
                        position: 'relative',
                        '&::-webkit-scrollbar': {
                          height: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1',
                          borderRadius: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#c1c1c1',
                          borderRadius: 4,
                          '&:hover': {
                            backgroundColor: '#a8a8a8',
                          },
                        },
                        // Add visual feedback when resizing
                        ...(resizing && {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(25, 118, 210, 0.05)',
                            pointerEvents: 'none',
                            zIndex: 999
                          }
                        })
                      }}
                    >
                      <Box sx={{ 
                        width: totalTableWidth, 
                        minWidth: totalTableWidth,
                        height: '100%'
                      }}>
                        <List
                          height={calculateVirtualScrollHeight()} // Use full available height
                          itemCount={sortedEventsForTable.length + 1} // +1 for header
                          itemSize={getItemSize} // Use function reference instead of inline function
                          itemData={{
                            events: sortedEventsForTable,
                            columns: displayColumns,
                            totalTableWidth,
                            sortColumn,
                            sortOrder,
                            isEditing: (eventId, column) => editingCell && editingCell.eventId === eventId && editingCell.column === column,
                            editValue,
                            allTagVariables,
                            tempRowHeights,
                            rowHeights,
                            getColumnWidth,
                            getTagValueForTable,
                            getTagValueAndObject, // Add optimized function to avoid double find() calls
                            handleCellClick,
                            handleRowResizeStart,
                            setEditValue,
                            saveEdit,
                            cancelEditing,
                            onColumnHeaderClick: handleColumnHeaderClick,
                            onResizeStart: handleResizeStart,
                            openTimeEventJsonViewer: openTimeEventJsonViewer,
                            caseid: caseid
                          }}
                          style={{ 
                            width: '100%',
                            height: '100%'
                          }}
                        >
                          {VirtualTableItem}
                        </List>
                      </Box>
                    </TableContainer>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Unified File Viewer System - replaces 12+ individual viewer components */}
      {fileViewer.open && (() => {
        const ViewerComponent = {
          pdf: PDFViewer,
          image: ImageViewer, 
          audio: AudioViewer,
          excel: ExcelViewer,
          html: HTMLViewer,
          text: TextViewer,
          video: VideoViewer,
          email: EmailViewer,
          zip: ZipViewer,
          word: WordViewer,
          powerpoint: PowerPointViewer,
          csv: CSVViewer,
          other: OtherViewer
        }[fileViewer.type] || OtherViewer;
        
        return (
          <ViewerComponent
            open={true}
            onClose={closeFileViewer}
            filename={fileViewer.filename}
            eventId={fileViewer.eventId}
          />
        );
      })()}

      {/* TimeEvent JSON Viewer for structured data in tooltips */}
      <TimeEventJsonViewer
        open={timeEventJsonViewerOpen}
        onClose={closeTimeEventJsonViewer}
        title={timeEventJsonTitle}
        data={timeEventJsonData}
        fieldId={timeEventJsonFieldId}
        onSave={handleTimeEventJsonSave}
        caseid={timeEventJsonCaseid}
        tag={timeEventJsonTag}
      />
    </Grid>
  );
};

export default EventList;
